import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '@chakra-ui/react'
import { AgentForm, FormData } from '../components/forms/AgentForm'
import { useAgent } from '../hooks/agents/useAgent'
import { useUser } from '../hooks/auth/useUser'
import { useLanguage } from '../hooks/utility/useLanguage'
import { UpdateAgentData } from '../api/modules/agentApi'
import { Agent, MAIN_CHAT_AGENT_SLUG, SWITCHED_TOOL_NAMES } from '../types/agentTypes'

const EditAgentPage = () => {
  const params = useParams<{ id?: string }>()
  const id = params.id || ''
  const navigate = useNavigate()
  const toast = useToast()
  const { t } = useLanguage()
  const { agent, isLoadingAgent, agentError, updateAgent, deleteAgent, isUpdatingAgent, isDeletingAgent } = useAgent(id)
  const { data: user } = useUser()
  const isSuperuser = !!user?.is_superuser

  // Handle agent loading error
  if (agentError) {
    toast({
      title: t('common.error'),
      description: t('editAgent.failedToLoad'),
      status: 'error',
      duration: 5000,
      isClosable: true,
    })
    navigate('/library', { replace: true })
    return null
  }

  const handleSubmit = async (data: FormData) => {
    if (!agent) return

    try {
      const {
        fileSearchEnabled,
        imageInspectionEnabled,
        knowledgeToolNames,
        switchedTools,
        knowledgeTopK,
        excludedFlagTypes,
        isPublic,
        clientEntityIds,
        availableInMainChat,
        routingDescription,
        useClientContext,
        ...rest
      } = data
      // The form represents FILE_SEARCH, INSPECT_IMAGE, SWITCHED_TOOLS and the
      // per-source `retrieve_knowledge_<source>` tools. Any other tool already
      // on the agent has no form control, so carry it through verbatim instead
      // of dropping it on save.
      const preservedTools = (agent.tools ?? []).filter(
        name =>
          name !== 'FILE_SEARCH' &&
          name !== 'INSPECT_IMAGE' &&
          !SWITCHED_TOOL_NAMES.includes(name) &&
          !name.startsWith('retrieve_knowledge_'),
      )
      const updateData: UpdateAgentData = {
        id: agent.id,
        ...rest,
        // Preserve the agent's existing type so editing a privileged
        // (sidebar) agent doesn't silently demote it to a normal one.
        type: agent.type,
        tools: [
          ...preservedTools,
          ...(fileSearchEnabled ? ['FILE_SEARCH'] : []),
          ...(imageInspectionEnabled ? ['INSPECT_IMAGE'] : []),
          ...switchedTools,
          ...knowledgeToolNames,
        ],
        is_public: isPublic,
        client_entity_ids: clientEntityIds,
        excluded_flag_types: excludedFlagTypes,
        knowledge_top_k: knowledgeTopK,
        available_in_main_chat: availableInMainChat,
        routing_description: routingDescription.trim() || null,
        use_client_context: useClientContext,
      }
      const result: Agent = await updateAgent(updateData)
      toast({
        title: t('editAgent.agentUpdated'),
        description: `${result.name} ${t('editAgent.updatedSuccessfully')}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      navigate('/library', { replace: true })
    } catch (error) {
      toast({
        title: t('editAgent.updateError'),
        description: t('editAgent.updateErrorDescription'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      throw error // Re-throw to let the form handle the error state
    }
  }

  const handleDelete = async () => {
    if (!agent) return

    try {
      await deleteAgent(agent.id)
      toast({
        title: t('editAgent.agentDeleted'),
        description: `${agent.name} ${t('editAgent.deletedSuccessfully')}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      navigate('/library', { replace: true })
    } catch (error) {
      toast({
        title: t('editAgent.deleteError'),
        description: t('editAgent.deleteErrorDescription'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      throw error // Re-throw to let the form handle the error state
    }
  }

  const isMainChat = agent?.slug === MAIN_CHAT_AGENT_SLUG

  // Prepare initial data for the form
  const initialData = agent ? {
    name: agent.name || '',
    description: agent.description || '',
    tags: agent.tags.map(tag => tag.name) || [],
    system_prompt: agent.system_prompt || '',
    profile_picture_url: agent.profile_picture_url || '',
    example_questions: agent.example_questions || [],
    fileSearchEnabled: (agent.tools ?? []).includes('FILE_SEARCH'),
    imageInspectionEnabled: (agent.tools ?? []).includes('INSPECT_IMAGE'),
    // Per-source knowledge tools stored on the agent. Preserved verbatim on
    // save even if the editing user can't see all of them (the form only
    // renders toggles for visible sources, but never drops the rest).
    knowledgeToolNames: (agent.tools ?? []).filter(name => name.startsWith('retrieve_knowledge_')),
    switchedTools: (agent.tools ?? []).filter(name => SWITCHED_TOOL_NAMES.includes(name)),
    // Retrieval width; null leaves the agent on the backend default.
    knowledgeTopK: agent.knowledge_top_k ?? null,
    // Flags the agent opts out of; everything else applies (the default).
    excludedFlagTypes: agent.excluded_flag_types ?? [],
    tracing_mode: agent.tracing_mode || ('tool_based' as const),
    isPublic: agent.is_public ?? false,
    clientEntityIds: agent.client_entity_ids ?? [],
    availableInMainChat: agent.available_in_main_chat ?? false,
    routingDescription: agent.routing_description ?? '',
    useClientContext: agent.use_client_context ?? false,
  } : {}

  return (
    <AgentForm
      mode="edit"
      initialData={initialData}
      isLoading={isLoadingAgent}
      onSubmit={handleSubmit}
      // Every client needs its main chat; the backend refuses to delete it too.
      onDelete={isMainChat ? undefined : handleDelete}
      isSubmitting={isUpdatingAgent}
      isDeleting={isDeletingAgent}
      isSuperuser={isSuperuser}
      isMainChat={isMainChat}
      title={t('editAgent.title')}
      subtitle={t('editAgent.subtitle')}
      submitButtonText={t('editAgent.updateAgent')}
    />
  )
}

export default EditAgentPage
